import type { ReportConsumptionCategory, ReportConsumptionInventory } from './types';
import { reportValue } from './valueHelpers';

export const CBEI_STORED_UNIT = 'grams CO2e per household per year';
export const CBEI_DISPLAY_UNIT = 'MT CO2e per community per year';

export interface CbeiRawRow {
  year: number;
  transportation_total_emissions: number | null;
  housing_total_emissions: number | null;
  food_total_emissions: number | null;
  goods_total_emissions: number | null;
  services_total_emissions: number | null;
  hhcount: number | null;
}

/** (stored grams per household / 1,000,000) × household count = MT CO2e per community per year */
export function scaleCbeiCategory(rawGramsPerHousehold: number | null, hhcount: number | null): number | null {
  if (rawGramsPerHousehold == null) return null;
  const hh = Number(hhcount);
  if (!Number.isFinite(hh) || hh <= 0) return null;
  return (Number(rawGramsPerHousehold) / 1_000_000) * hh;
}

export function buildConsumptionInventoryFromCbei(
  rows: CbeiRawRow[],
  inventoryYear: number
): ReportConsumptionInventory {
  const row = rows.find((r) => r.year === inventoryYear);
  if (!row) {
    return {
      sectionLabel: 'Household consumption inventory',
      modelName: null,
      modelVersion: null,
      inventoryYear: null,
      storedUnit: CBEI_STORED_UNIT,
      displayUnit: CBEI_DISPLAY_UNIT,
      scalingMethod: '(grams per household / 1,000,000) × hhcount',
      householdCount: reportValue<number | null>(null, 'households', 'missing', { sourceTable: 'cbei_cities', sourceColumn: 'hhcount' }),
      categories: [],
      totalMt: reportValue<number | null>(null, CBEI_DISPLAY_UNIT, 'missing', { sourceTable: 'cbei_cities' }),
      suppressed: true,
      suppressionReason: 'No CBEI row for inventory year',
    };
  }

  const hh = Number(row.hhcount);
  const hhValid = Number.isFinite(hh) && hh > 0;
  const cats: Array<{ code: string; label: string; field: keyof CbeiRawRow }> = [
    { code: 'transportation', label: 'Transportation', field: 'transportation_total_emissions' },
    { code: 'housing', label: 'Housing', field: 'housing_total_emissions' },
    { code: 'food', label: 'Food', field: 'food_total_emissions' },
    { code: 'goods', label: 'Goods', field: 'goods_total_emissions' },
    { code: 'services', label: 'Services', field: 'services_total_emissions' },
  ];

  const categories: ReportConsumptionCategory[] = cats.map((c) => {
    const raw = row[c.field] as number | null;
    const scaled = hhValid ? scaleCbeiCategory(raw, hh) : null;
    return {
      categoryCode: c.code,
      categoryLabel: c.label,
      displayTotalMt: reportValue(scaled, CBEI_DISPLAY_UNIT, scaled == null ? 'missing' : 'modeled', {
        sourceTable: 'cbei_cities',
        sourceColumn: String(c.field),
        calculationRule: '(grams per household / 1e6) × cbei_cities.hhcount',
      }),
      rawGramsPerHousehold: raw,
    };
  });

  const total = categories.every((c) => c.displayTotalMt.value != null)
    ? categories.reduce((a, c) => a + (c.displayTotalMt.value ?? 0), 0)
    : null;

  const suppressed = !hhValid || total == null;

  return {
    sectionLabel: 'Household consumption inventory',
    modelName: null,
    modelVersion: null,
    inventoryYear,
    storedUnit: CBEI_STORED_UNIT,
    displayUnit: CBEI_DISPLAY_UNIT,
    scalingMethod: '(grams per household / 1,000,000) × cbei_cities.hhcount for inventory year; suppressed when hhcount missing or ≤ 0',
    householdCount: reportValue(hhValid ? hh : null, 'households', hhValid ? 'modeled' : 'missing', {
      sourceTable: 'cbei_cities',
      sourceColumn: 'hhcount',
      sourceYear: inventoryYear,
      calculationRule: 'CBEI model household count; not a Census observation',
    }),
    categories,
    totalMt: reportValue(suppressed ? null : total, CBEI_DISPLAY_UNIT, suppressed ? 'missing' : 'modeled', {
      sourceTable: 'cbei_cities',
      calculationRule: 'sum of scaled categories',
    }),
    suppressed,
    suppressionReason: suppressed ? (hhValid ? 'Incomplete category data' : 'Household count missing or nonpositive in cbei_cities for inventory year') : undefined,
  };
}
